import { useEffect, useState } from "react";
import { Reveal } from "@/components/Motion";
import { TEAM } from "@/data/site";
import { API } from "@/data/site";

const initials = (name) =>
  name
    .replace("Dr.", "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export default function Team() {
  const [members, setMembers] = useState(TEAM.map((m) => ({ ...m, member_key: m.name.toLowerCase().replace(/[^a-z]+/g, "-") })));

  useEffect(() => {
    fetch(`${API}/api/team`)
      .then((r) => r.json())
      .then(setMembers)
      .catch(() => {});
  }, []);

  return (
    <div className="pt-16" data-testid="team-page">
      <section className="lab-grid border-b border-[#e4e9ec]">
        <div className="mx-auto max-w-[1440px] px-5 pb-16 pt-20 md:px-10 md:pb-20 md:pt-24">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Exoplus Bio</p>
            <h1 className="font-display mt-4 text-[clamp(2.6rem,6vw,5rem)] leading-[0.98] tracking-tight" data-testid="team-headline">
              Meet the <em>team</em>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#56636b]" data-testid="team-intro">
              The people behind Exoplus Bio — leadership, science, compliance and operations united by one standard:
              elevate your aesthetics, trust in science.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {members.map((m, i) => (
            <Reveal key={m.name} delay={(i % 3) * 0.08}>
              <div
                className="group relative h-full border border-[#e4e9ec] bg-white p-8 transition-colors duration-300 hover:border-[#0a0a0a] hover:bg-[#0a0a0a]"
                data-testid={`team-card-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-lab text-[10px] tracking-[0.2em] text-[#0e8c8c]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-lab text-[10px] uppercase tracking-[0.16em] text-[#56636b] transition-colors group-hover:text-white/40">
                    Exoplus Bio
                  </span>
                </div>
                <div className="mt-10 h-24 w-24">
                  {m.photo_url ? (
                    <img
                      src={m.photo_url}
                      alt={m.name}
                      data-testid={`team-photo-${m.member_key || "member"}`}
                      className="h-24 w-24 rounded-full border border-[#e4e9ec] object-cover transition-colors duration-300 group-hover:border-[#0e8c8c]/60"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-full border border-[#e4e9ec] transition-colors duration-300 group-hover:border-[#0e8c8c]/60 group-hover:bg-[#0e8c8c]/10">
                      <span className="font-display text-3xl transition-colors duration-300 group-hover:text-[#0e8c8c]">
                        {initials(m.name)}
                      </span>
                    </div>
                  )}
                </div>
                <h2 className="font-display mt-8 text-2xl leading-snug transition-colors duration-300 group-hover:text-white" data-testid="team-member-name">
                  {m.name}
                </h2>
                <p className="font-lab mt-2 text-[11px] uppercase tracking-[0.14em] text-[#56636b] transition-colors duration-300 group-hover:text-white/60" data-testid="team-member-role">
                  {m.role}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mx-auto mt-20 max-w-2xl text-center text-sm leading-relaxed text-[#56636b]">
            Questions about our team, science or partnerships? Reach us at{" "}
            <a href="mailto:info@exoplusbio.com" className="text-[#0e8c8c] underline-offset-4 hover:underline" data-testid="team-contact-email">
              info@exoplusbio.com
            </a>
          </p>
        </Reveal>
      </section>
    </div>
  );
}